import * as ko from 'knockout';

import i18n from '../i18n';
import { MaybeKO, asObservable } from '../utils/ko_utils';
import { I18nText, translate } from '../i18n_text';

let template = require('raw-loader!../../templates/components/form_nested_entities.html').default;

interface FormNestedEntityAction<T> {
  callback(entity: T): void;
  icon: string;
}

export interface FormNestedEntitiesDisableSupport<T> {
  /** Current state; drives the icon and the row-greying `disabled` css class. */
  disabled(entity: T): boolean;
  /** Omit, or return false, to render a read-only indicator instead of a toggle. */
  canDisable?(entity: T): boolean;
  /** Required to make the icon clickable. */
  disable?(entity: T): void;
}

export interface FormNestedEntitiesConfiguration<T> {
  initialSelection?: T;

  title: ko.Computed<string> | MaybeKO<string>;
  addTitle: ko.Computed<string> | MaybeKO<string>;
  missingTitle: ko.Computed<string> | MaybeKO<string>;
  infoText?: ko.Computed<string> | MaybeKO<string>;

  entities: ko.Computed<T[]> | KnockoutObservable<T[]>;

  canRemove(entity: T): boolean;

  add(): T;
  remove(entity: T): void;

  /**
   * Opt-in visibility ("eye") affordance. Omit for entity types with no
   * hidden/visible concept: no icon is rendered at all. Supplying it renders the
   * eye, clickable when `canDisable(entity)` is true, otherwise a read-only
   * indicator shown only while the entity is hidden.
   */
  disableSupport?: FormNestedEntitiesDisableSupport<T>;

  hasErrors(entity: T): boolean;
  showErrors(entity: T): void;

  actions: FormNestedEntityAction<T>[];

  getSummaryName(entity: T): string | I18nText;

  selectedEntity?: ko.Observable<T>;
}

export class FormNestedEntities<T> {
  config: FormNestedEntitiesConfiguration<T>;
  enable: KnockoutObservable<boolean>;
  selectedEntity: ko.Observable<T>;

  constructor(params: { config: FormNestedEntitiesConfiguration<T>; enable?: MaybeKO<boolean> }) {
    this.config = params.config;

    this.selectedEntity = params.config.selectedEntity || ko.observable<T>(null);

    if (this.config.initialSelection) {
      this.selectedEntity(this.config.initialSelection);
    }
    if (params.enable !== undefined) {
      this.enable = asObservable(params.enable);
    } else {
      this.enable = ko.observable(true);
    }
  }

  add = () => {
    let entity = this.config.add();
    this.selectedEntity(entity);
  };

  canRemove = (entity: T) => {
    return this.config.canRemove(entity) && this.enable();
  };

  remove = (entity: T, event: Event) => {
    event.stopPropagation();

    if (this.selectedEntity() == entity) {
      this.selectedEntity(null);
    }

    this.config.remove(entity);
  };

  // Deliberately a getter: `config` is assigned in the constructor body, so a
  // field initializer would run first and read `undefined`. An arrow function
  // would be worse — Knockout's `if:` binding does not call a plain function, so
  // the gate would be unconditionally truthy.
  get supportsDisable(): boolean {
    return !!this.config.disableSupport;
  }

  canDisable = (entity: T) => {
    const support = this.config.disableSupport;
    return !!support?.disable && (support.canDisable?.(entity) ?? false) && this.enable();
  };
  disabled = (entity: T) => {
    return this.config.disableSupport?.disabled(entity) ?? false;
  };
  disable = (entity: T, event: Event) => {
    event.stopPropagation();
    this.config.disableSupport?.disable?.(entity);
  };

  hideTitle = i18n.t('Hide');
  unhideTitle = i18n.t('Unhide');
  hiddenTitle = i18n.t('Hidden');

  invokeAction = (entity: T, action: FormNestedEntityAction<T>, evt: Event) => {
    evt.stopPropagation();
    action.callback(entity);
  };

  isSelected = (entity: T) => {
    return this.selectedEntity() == entity;
  };

  showErrorsInTitle = (entity: T) => {
    return !this.isSelected(entity) && this.config.hasErrors(entity);
  };

  toggleSelection = (entity: T) => {
    if (this.isSelected(entity)) {
      this.selectedEntity(null);
    } else {
      this.selectedEntity(entity);
      this.config.showErrors(entity);
    }
  };

  hasSummaryName(entity: T): boolean {
    let name = this.config.getSummaryName(entity);
    return name && (typeof name === 'string' || !!translate(name));
  }
}

ko.components.register('form-nested-entities', {
  viewModel: FormNestedEntities,
  template: template,
});
